import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { InlineDatePicker } from "../../../components/ui";
import {
  ChevronLeft,
  ChevronRight,
} from "../../../components/icons/svg/outline";
import useOutsideClick from "../../../hooks/useOutsideClick";
import {
  getDateInLocalTZFromDateStr,
  getDateString,
} from "../../../utils/datetime";

/**
 * Week navigation for the Dispatch: previous week, jump to a picked date's
 * week, and next week. "Set Date" opens {@link InlineDatePicker}; Today in
 * that panel jumps to the current week.
 *
 * @component
 * @param {void}
 * @returns {JSX.Element} The rendered week switcher.
 */
const DispatchSwitcher = () => {
  const {
    date,
    resetWeekStart,
    goToWeekContaining,
    goToNextWeek,
    goToPrevWeek,
  } = useContext(WeeklyContext);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });
  const rootRef = useRef(null);
  const closePicker = useCallback(() => setIsPickerOpen(false), []);
  useOutsideClick(rootRef, closePicker, {
    ignoreSelector: "[data-dispatch-date-picker-panel]",
  });

  /**
   * Anchor the date-picker panel under the Set Date button (right-aligned).
   *
   * @returns {void}
   */
  const updatePanelPosition = useCallback(() => {
    if (!rootRef.current) {
      return;
    }
    const rect = rootRef.current.getBoundingClientRect();
    setPanelPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isPickerOpen) {
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [isPickerOpen, updatePanelPosition]);

  /**
   * Jump to the week that contains the picked calendar day and close the panel.
   *
   * @param {string} dateKey YYYY-MM-DD.
   * @returns {void}
   */
  const handlePickDate = (dateKey) => {
    goToWeekContaining(getDateInLocalTZFromDateStr(dateKey));
    setIsPickerOpen(false);
  };

  /**
   * Jump to this week's start (same as the former Today control) and close.
   *
   * @returns {void}
   */
  const handleToday = () => {
    resetWeekStart();
    setIsPickerOpen(false);
  };

  return (
    <div ref={rootRef} className="relative font-inter flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={() => goToPrevWeek()}
        aria-label="Previous week"
        title="Previous week"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-200"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => setIsPickerOpen((open) => !open)}
        aria-haspopup="dialog"
        aria-expanded={isPickerOpen}
        aria-label="Set date"
        title="Set date"
        className="h-6 shrink-0 rounded border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
      >
        Select Date
      </button>
      <button
        type="button"
        onClick={() => goToNextWeek()}
        aria-label="Next week"
        title="Next week"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-200"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
      {isPickerOpen
        ? createPortal(
            <div
              data-dispatch-date-picker-panel
              role="dialog"
              aria-label="Choose a date"
              className="fixed z-[80] w-[17.5rem] font-inter overflow-hidden rounded-lg border border-gray-200 bg-white p-3 shadow-lg shadow-primary-900/10"
              style={{
                top: panelPosition.top,
                right: panelPosition.right,
              }}
            >
              <button
                type="button"
                onClick={handleToday}
                className="mb-2 h-6 w-full rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:bg-gray-200"
              >
                Today
              </button>
              <InlineDatePicker
                value={getDateString(date)}
                onChange={handlePickDate}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default DispatchSwitcher;
